// The requisition's milestones (DS §4 B2 row: "milestone timeline … tabs … Timeline"): the seven stations of DC-30 as a
// MilestoneTimeline (row 38) with the Hold · Cancel · Reopen · Archive diamonds and the hold span, and the Requisition
// timeline tab, a History (row 69) over the spine's `timeline.requisition` rows with actor and date ("Requisition
// timeline: request through approval/posting/intake/panel/offer/filled/hold/cancel/reopen/archive."). The stations are
// read from the record — the request's creation (the Request station's date, from the request's own read where the
// seat may open it), the request's approval, the posting, the intake record the Intake & Panel page reads (Intake at the
// intake call held, Panel once the panel is finalized: F-48), the V1 offer checkpoint, and the closeout — never computed
// beyond what it says. A seat that may not open the intake sees its two stations from the record's own evidence of a
// finalized intake, undated, never another event's date. A timeline row is drawn in the words the server gives
// the record's milestones; a row with no word of his yet (and `hold_pending`, a span's end) is left out.
// The tab's body is a slot (C-B10-13): a body registered with `registerTimelineBody` (slots.ts) replaces the History
// below, and the Activity link stays under it.

import {
  intakeOpen, requestOpen, timelineRequisition, type IntakeOpenOutput, type MilestoneOut, type RequisitionFull, type TimelineRequisitionOutput,
} from "@/api/tools";
import { useSession } from "@/session/useSession";
import {
  ErrorStrip, History, HISTORY_LABELS, MILESTONE_LABELS, MilestoneTimeline, RULE_LABELS, TextLink, formatDate,
  type HistoryEvent, type MilestoneEvent, type MilestoneStation, type Tone,
} from "@/mc";
import { useSettledRead } from "./data";
import { timelineBody } from "./slots";

// REBASE (B4): the intake plan's own milestone replaces this. Until then, for a seat that may not open the intake, a
// finalized intake is read from the server's stage projection (`requisition._stage`), compared, never drawn.
const INTAKE_FINALIZED = "Intake finalized — sourcing cleared";

/** What the stations read from the intake record (`intake.open`, the Intake & Panel page's read). */
export type IntakeFacts = Pick<IntakeOpenOutput, "status" | "meeting" | "finalized_at">;

interface Dated { at: string | null; done: boolean }

/** The Intake and Panel stations: from the intake record when the seat may open it — the intake call held (else the
 * finalization) and the panel finalized, so a reopened panel is current again — else from the record's own evidence,
 * undated: a finalized intake (the stage, or the first slate that went out after it) and the onsite interviews held. */
function intakeStations(r: RequisitionFull, intake: IntakeFacts | null): { intake: Dated; panel: Dated } {
  if (intake) {
    const held = typeof intake.meeting?.["held_at"] === "string" ? intake.meeting["held_at"] : null;
    const intakeAt = held ?? intake.finalized_at;
    const finalized = intake.status === "finalized" && intake.finalized_at !== null;
    return { intake: { at: intakeAt, done: intakeAt !== null }, panel: { at: finalized ? intake.finalized_at : null, done: finalized } };
  }
  return {
    intake: { at: null, done: checkpoint(r, "sourcing") !== null || r.stage === INTAKE_FINALIZED },
    panel: { at: null, done: checkpoint(r, "onsite_interviews") !== null },
  };
}

const CLOSED = new Set(["closed_cancelled", "closed_filled", "filled", "archived"]);

function at(history: readonly MilestoneOut[], key: string): MilestoneOut | undefined {
  return [...history].reverse().find((row) => row.key === key && row.status !== "reversed");
}

function checkpoint(r: RequisitionFull, name: string): string | null {
  return r.checkpoints.find((c) => c.name === name)?.occurred_at ?? null;
}

interface Track {
  stations: MilestoneStation[];
  events: MilestoneEvent[];
  holdSpan?: { from: number; to: number } | undefined;
  holdDays?: number | undefined;
}

/** The stations, the lifecycle diamonds and the hold span of one requisition, in its viewer's time zone; `requestAt` is
 * when its request was created (the Request station's date; undated without it) and `intake` the intake record (the
 * Intake and Panel stations' dates; the record's evidence, undated, without it). */
export function trackOf(r: RequisitionFull, timeZone: string, requestAt: string | null = null, intake: IntakeFacts | null = null): Track {
  const history = r.milestone_history ?? [];
  const approved = at(history, "request_approved")?.at ?? null;
  const filled = at(history, "filled")?.at ?? (r.status === "filled" || r.status === "closed_filled" ? r.updated_at : null);
  const plan = intakeStations(r, intake);
  const dated: { label: string; at: string | null; done: boolean }[] = [
    { label: MILESTONE_LABELS.request, at: requestAt, done: true },
    { label: MILESTONE_LABELS.approval, at: approved, done: true },
    { label: MILESTONE_LABELS.posting, at: r.posted_at, done: r.posted_at !== null },
    { label: MILESTONE_LABELS.intake, ...plan.intake },
    { label: MILESTONE_LABELS.panel, ...plan.panel },
    { label: MILESTONE_LABELS.offer, at: checkpoint(r, "offer"), done: checkpoint(r, "offer") !== null },
    { label: MILESTONE_LABELS.filled, at: filled, done: filled !== null },
  ];
  const live = !CLOSED.has(r.status) && r.state_archive !== "archived";
  const next = dated.findIndex((station) => !station.done);
  const stations: MilestoneStation[] = dated.map((station, index) => ({
    label: station.label,
    state: station.done ? "done" : live && index === next ? (r.status === "on_hold" ? "hold" : "active") : "next",
    date: station.at ? formatDate(station.at, timeZone) : undefined,
  }));
  // where an instant falls on the track: just after the last station reached by then
  const place = (instant: string): number => {
    let last = 0;
    dated.forEach((station, index) => { if (station.at !== null && station.at <= instant) last = index; });
    return Math.min(dated.length - 1, last + 0.5);
  };
  const events: MilestoneEvent[] = [];
  let holdSpan: Track["holdSpan"];
  for (const row of history) {
    if (row.status === "reversed") continue;
    if (row.key === "on_hold" && row.status === "reached") {
      events.push({ kind: "hold", at: place(row.at), date: formatDate(row.at, timeZone) });
      const ended = history.find((other) => other.key === "on_hold" && other.status === "ended" && other.at >= row.at);
      const from = place(row.at);
      const to = ended ? place(ended.at) : next >= 0 ? next : dated.length - 1;
      holdSpan = { from: Math.min(from, to) - 0.2, to: Math.max(from, to) + 0.2 };
    }
    if (row.key === "cancelled" && row.status === "reached") events.push({ kind: "cancel", at: place(row.at), date: formatDate(row.at, timeZone) });
    if (row.key === "reopened" && row.status === "reached") events.push({ kind: "reopen", at: place(row.at), date: formatDate(row.at, timeZone) });
    if (row.key === "archived" && row.status === "reached") events.push({ kind: "archive", at: place(row.at), date: formatDate(row.at, timeZone) });
  }
  if (r.state_archive === "archived" && r.archived_at && !events.some((event) => event.kind === "archive")) {
    events.push({ kind: "archive", at: place(r.archived_at), date: formatDate(r.archived_at, timeZone) });
  }
  const acceptance = (r.time_clocks ?? []).find((clock) => clock.clock_key === "time_to_acceptance");
  return { stations, events, holdSpan, holdDays: r.status === "on_hold" ? acceptance?.hold_days ?? undefined : undefined };
}

export function RequisitionTrack({ r, timeZone }: { r: RequisitionFull; timeZone: string }) {
  const { can } = useSession();
  // the request's own read (shared with the Hiring Request's page); a seat that may not open it sees the station undated
  const request = useSettledRead(["request", r.request_id], () => requestOpen({ request_id: r.request_id }), { enabled: can("request.open") });
  // the intake record the Intake & Panel page reads (its query, so a write there moves the stations); a seat not seated
  // on the intake reads the record's evidence instead
  const intake = useSettledRead(["intake", r.id], () => intakeOpen({ requisition_id: r.id }), { enabled: can("intake.open") && Boolean(r.can.intake) });
  const track = trackOf(r, timeZone, request.data?.created_at ?? null, intake.data ?? null);
  return <MilestoneTimeline label={HISTORY_LABELS.requisitionTimeline} stations={track.stations} events={track.events} holdSpan={track.holdSpan} holdDays={track.holdDays} />;
}

/** The governance and creation words carry their own duty hue (DS §1.4); the rest are blue. */
const TONE_OF: Readonly<Record<string, Tone>> = {
  on_hold: "warn", cancellation_pending: "warn", reopen_pending: "warn", cancelled: "neutral", archived: "neutral", reopened: "info",
};

/** His word for each milestone key, as the server labels the record's milestones (`milestone_history`). */
export function milestoneWords(r: RequisitionFull): ReadonlyMap<string, string> {
  const words = new Map<string, string>();
  for (const row of r.milestone_history ?? []) if (row.label !== null && row.key !== "hold_pending") words.set(row.key, row.label);
  return words;
}

/** The spine's rows as History events: each reached milestone the record names, newest first. */
export function timelineEvents(rows: TimelineRequisitionOutput["rows"], words: ReadonlyMap<string, string>): HistoryEvent[] {
  const events: HistoryEvent[] = [];
  for (const row of rows) {
    // `milestone.<key>` is a reached milestone; `.ended` and `.reversed` (a span's end, a correction) have no word
    const key = row.kind.startsWith("milestone.") ? row.kind.slice("milestone.".length) : null;
    const sentence = key !== null ? words.get(key) : undefined;
    if (!sentence) continue;
    events.push({
      id: row.id,
      actor: row.actor_name ? { name: row.actor_name } : undefined,
      sentence,
      at: row.at,
      tone: TONE_OF[key ?? ""] ?? "info",
    });
  }
  return events;
}

/** The Requisition timeline tab: a registered body (slots.ts) when a lane mounted one, else the spine's history. */
export function RequisitionTimeline({ r, timeZone, auditHref }: { r: RequisitionFull; timeZone: string; auditHref?: string | undefined }) {
  const Body = timelineBody.list()[0]?.component;
  if (Body) {
    return (
      <>
        <Body r={r} timeZone={timeZone} auditHref={auditHref} />
        {auditHref ? <TextLink href={auditHref} >{HISTORY_LABELS.audit}</TextLink> : null}
      </>
    );
  }
  return <SpineHistory r={r} timeZone={timeZone} auditHref={auditHref} />;
}

/** The spine's history of the requisition, newest first, with its actor and date. */
function SpineHistory({ r, timeZone, auditHref }: { r: RequisitionFull; timeZone: string; auditHref?: string | undefined }) {
  const q = useSettledRead(["timeline", r.id], () => timelineRequisition({ requisition_id: r.id, limit: 500 }));
  const events = q.data ? timelineEvents(q.data.rows, milestoneWords(r)) : [];
  return (
    <>
      {q.error ? <ErrorStrip message={q.error.remedy || RULE_LABELS.failedSyncRetryable} onRetry={() => void q.refetch()} /> : null}
      <History label={HISTORY_LABELS.requisitionTimeline} events={events} timeZone={timeZone} loading={q.isPending} />
      {auditHref ? <TextLink href={auditHref} >{HISTORY_LABELS.audit}</TextLink> : null}
    </>
  );
}
